import {
	updateCurrentUserApiUsersMePatch,
	getGetCurrentUserInfoApiUsersMeGetQueryKey
} from '$lib/api/generated/users/users';
import { getQueryClient } from '$lib/query/client';
import { authStore, captureTenantSession, isTenantSessionCurrent } from '$lib/stores/auth';
import { timezoneSchema } from '$lib/schemas/preferences';

/**
 * Persist the user's timezone preference and keep client state coherent.
 *
 * Mirrors the backend write boundary: a non-null value is validated against the
 * IANA allow-list before the PATCH (throws on bad input -> caller toasts the
 * error), so the request never carries garbage even if a future caller passes
 * a free-form value. The PATCH response is itself the fresh `UserRead`, so we
 * push it straight into the TanStack cache and the auth store — no extra
 * round-trip, and we sidestep `initAuth()`'s `staleTime` (which would otherwise
 * return the stale cached user right after the write). Pass `null` to clear.
 */
export async function saveTimezone(timezone: string | null): Promise<boolean> {
	if (timezone !== null) {
		timezoneSchema.parse(timezone);
	}
	const session = captureTenantSession();
	const updated = await updateCurrentUserApiUsersMePatch({ timezone });
	if (!isTenantSessionCurrent(session)) return false;

	const queryClient = getQueryClient();
	queryClient.setQueryData(getGetCurrentUserInfoApiUsersMeGetQueryKey(), updated);

	authStore.update((state) =>
		state.user
			? { ...state, user: { ...state.user, timezone: updated.timezone ?? undefined } }
			: state
	);
	return true;
}
